import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, RotateCw, AlertTriangle, CheckCircle2, Info, ArrowLeft, FileCode2, Languages, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

// ─── Utility ────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "adesso";
  if (mins < 60) return `${mins}min fa`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h fa`;
  const days = Math.floor(hrs / 24);
  return `${days}g fa`;
};

// ─── Main page ──────────────────────────────────────────
const AdminManualSvgsStatusPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/manuals/svgs-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento stato SVG");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleRegenerate = async () => {
    if (!window.confirm("Rigenerare tutti gli SVG del manuale? L'operazione è idempotente (produce file byte-identici se nessun plugin è stato modificato) ma verrà registrata in audit.")) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/admin/manuals/svgs/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ Rigenerati ${res.data.total_svgs_written} SVG in ${res.data.elapsed_ms}ms`);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rigenerazione");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <RefreshCw className="animate-spin text-lojack-red" size={32} />
      </div>
    );
  }

  if (!data) return null;

  const { plugins, summary } = data;
  const healthOk = !summary.any_stale && !summary.any_missing_langs && !summary.any_missing_whitelist;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6" data-testid="admin-manual-svgs-status-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/admin/manuals")} className="text-gray-400 hover:text-white transition" data-testid="back-to-manuals">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileCode2 className="text-lojack-red" size={24} />
              Stato SVG Manuali Operativi
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Registry dei plug-in localizzati per le illustrazioni SVG del manuale dealer. {summary.plugin_count} plug-in registrati, {summary.total_svg_count} file SVG su disco.
            </p>
          </div>
        </div>

        {/* Summary health card */}
        <div className={`p-4 rounded-xl border mb-6 ${healthOk ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-lojack-clause-500/5 border-lojack-clause-500/30'}`} data-testid="summary-health-card">
          <div className="flex items-start gap-3">
            {healthOk ? (
              <CheckCircle2 className="text-emerald-400 flex-shrink-0 mt-0.5" size={20} />
            ) : (
              <AlertTriangle className="text-lojack-clause-400 flex-shrink-0 mt-0.5" size={20} />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${healthOk ? 'text-emerald-300' : 'text-lojack-clause-300'}`}>
                {healthOk ? "Registry sano — nessuna deriva rilevata" : "Attenzione: alcuni plug-in richiedono azione"}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className={`px-2 py-1 rounded-full ${summary.any_stale ? 'bg-lojack-clause-500/15 text-lojack-clause-300' : 'bg-emerald-500/15 text-emerald-300'}`} data-testid="badge-stale">
                  {summary.any_stale ? "⚠ SVG obsoleti (regen necessaria)" : "✓ SVG sincronizzati"}
                </span>
                <span className={`px-2 py-1 rounded-full ${summary.any_missing_langs ? 'bg-lojack-clause-500/15 text-lojack-clause-300' : 'bg-emerald-500/15 text-emerald-300'}`} data-testid="badge-missing-langs">
                  {summary.any_missing_langs ? "⚠ Lingue SOC mancanti" : "✓ 18 SOC lingue coperte"}
                </span>
                <span className={`px-2 py-1 rounded-full ${summary.any_missing_whitelist ? 'bg-lojack-clause-500/15 text-lojack-clause-300' : 'bg-emerald-500/15 text-emerald-300'}`} data-testid="badge-whitelist">
                  {summary.any_missing_whitelist ? "⚠ Plug-in fuori whitelist backend" : "✓ Whitelist allineata"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Regenerate CTA */}
        <div className="p-4 rounded-xl border border-lojack-red/30 bg-lojack-red/5 mb-6 flex items-center justify-between gap-4">
          <div className="flex-1 flex items-start gap-3">
            <Info className="text-lojack-red flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-white text-sm">Rigenera tutti gli SVG</p>
              <p className="text-gray-400 text-xs mt-1">
                Esegue <code className="bg-black/40 px-1 rounded text-lojack-red">driver.generate_all()</code> lato backend. Deterministico + idempotente: se nessun plug-in è stato modificato produce file byte-identici. Utile dopo aver editato un file plug-in via SSH o Git. Operazione registrata in <code className="bg-black/40 px-1 rounded">admin_audit</code>.
              </p>
            </div>
          </div>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="btn-lojack flex items-center gap-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="btn-regenerate-svgs"
          >
            {regenerating ? (
              <><RefreshCw className="animate-spin" size={16} /> Rigenerazione…</>
            ) : (
              <><RotateCw size={16} /> Rigenera tutti</>
            )}
          </button>
        </div>

        {/* Plug-in table */}
        <div className="border border-gray-800 rounded-xl overflow-hidden" data-testid="plugins-table">
          <table className="w-full text-sm">
            <thead className="bg-[#111] text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Plug-in</th>
                <th className="text-left px-4 py-3 font-semibold"><span className="inline-flex items-center gap-1"><Languages size={12} /> Lingue</span></th>
                <th className="text-left px-4 py-3 font-semibold"><span className="inline-flex items-center gap-1"><Clock size={12} /> Sorgente</span></th>
                <th className="text-left px-4 py-3 font-semibold"><span className="inline-flex items-center gap-1"><FileCode2 size={12} /> SVG generati</span></th>
                <th className="text-center px-4 py-3 font-semibold">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {plugins.map(p => {
                const missing = p.missing_soc_langs?.length > 0;
                const notWhitelisted = !p.in_backend_whitelist;
                const rowOk = !p.stale && !missing && !notWhitelisted;
                return (
                  <tr key={p.name} className="hover:bg-[#111]/50 transition" data-testid={`plugin-row-${p.name}`}>
                    <td className="px-4 py-3">
                      <div className="font-mono text-white font-semibold">{p.name}</div>
                      <div className="text-gray-500 text-[11px] mt-0.5">{p.source_file}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-bold text-lg leading-none" data-testid={`plugin-lang-count-${p.name}`}>{p.language_count}</div>
                      <div className="text-gray-500 text-[10px] mt-1">
                        {p.languages.slice(0, 6).join(", ")}{p.languages.length > 6 ? `, +${p.languages.length - 6}` : ""}
                      </div>
                      {missing && (
                        <div className="text-lojack-clause-400 text-[10px] mt-1" data-testid={`plugin-missing-langs-${p.name}`}>
                          ⚠ manca: {p.missing_soc_langs.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-300 text-xs">{fmtDate(p.source_mtime)}</div>
                      <div className="text-gray-500 text-[10px] mt-0.5">{relativeTime(p.source_mtime)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-bold" data-testid={`plugin-svg-count-${p.name}`}>{p.svg_count}</div>
                      <div className="text-gray-500 text-[10px] mt-0.5">
                        {p.svg_latest_mtime ? fmtDate(p.svg_latest_mtime) : "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {rowOk ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-xs" data-testid={`plugin-status-${p.name}`}>
                          <CheckCircle2 size={14} /> OK
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1 items-center">
                          {p.stale && <span className="text-lojack-clause-400 text-[10px]" data-testid={`plugin-stale-${p.name}`}>⚠ obsoleto</span>}
                          {notWhitelisted && <span className="text-lojack-clause-400 text-[10px]">⚠ non in whitelist</span>}
                          {missing && <span className="text-lojack-clause-400 text-[10px]">⚠ lingue mancanti</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer info */}
        <div className="mt-6 p-4 rounded-xl bg-[#111] border border-gray-800 text-xs text-gray-400">
          <p className="font-semibold text-gray-300 mb-2">Come aggiungere un nuovo mockup:</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>Duplica <code className="bg-black/40 px-1 rounded text-white">/app/backend/scripts/manual_svg_plugins/_template.py</code></li>
            <li>Modifica <code className="bg-black/40 px-1 rounded text-white">NAME</code>, <code className="bg-black/40 px-1 rounded text-white">TRANSLATIONS</code> (obbligatorio: chiave <code className="bg-black/40 px-1 rounded text-white">IT</code>) e la funzione <code className="bg-black/40 px-1 rounded text-white">render()</code></li>
            <li>Aggiungi il nuovo <code className="bg-black/40 px-1 rounded text-white">NAME</code> a <code className="bg-black/40 px-1 rounded text-white">_LOCALIZED_SCREENSHOTS</code> in <code className="bg-black/40 px-1 rounded text-white">routes/operational_manual.py</code></li>
            <li>Referenzia l&apos;asset dal markdown master (es. <code className="bg-black/40 px-1 rounded text-white">/manual/screenshots/my-asset.svg</code>) — il backend riscrive automaticamente per le 18 SOC lingue</li>
            <li>Clicca <strong>&quot;Rigenera tutti&quot;</strong> qui sopra — auto-discovery raccoglie il nuovo plug-in senza bisogno di redeploy backend</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminManualSvgsStatusPage;
